import { useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import PageWrapper from "@/components/PageWrapper.tsx";
import BaseButton from "@/components/base/button/BaseButton.tsx";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import IconCreditCard from "@/assets/icons/credit-card.svg?react";
import { Trash2, Plus, Phone, ArrowRight } from "lucide-react";
import BankCardItem from "./components/BankCardItem.tsx";
import {
  extractErrorMessage,
  useDeleteCard,
  useMyCards,
  useSetDefaultCard,
} from "./api.ts";
import { useGetProfile, useSharePhone } from "@/pages/profile/api";
import showCustomToast from "@/shared/utils/showCustomToast.ts";

export default function MyCardsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: profile } = useGetProfile();
  const hasPhone = Boolean(profile?.phone);
  const { sharePhone, isPending: isSharingPhone } = useSharePhone({
    onSuccess: () => navigate("/profile/cards/add"),
  });

  const { data: cards = [], isLoading } = useMyCards();
  const { mutate: deleteCard, isPending: isDeleting } = useDeleteCard();
  const { mutate: setDefault, isPending: isSettingDefault } = useSetDefaultCard();

  const [deleteMode, setDeleteMode] = useState(false);
  const hasCards = cards.length > 0;
  const mutating = isDeleting || isSettingDefault;

  if (!hasPhone) {
    return (
      <PageWrapper backPath="/profile">
        <div className="container pt-safe-top pb-safe-bottom fixed inset-0 flex flex-col">
          <div
            className="flex items-center justify-between gap-3 py-3"
            aria-hidden="true"
          >
            <h1 className="text-lg font-semibold leading-130 invisible">
              {t("my_cards") || "Mening kartalarim"}
            </h1>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-5">
            <div className="size-20 rounded-full bg-orange-100/15 border border-orange-100/30 flex-center">
              <Phone className="size-10 text-orange-100" strokeWidth={1.5} />
            </div>
            <div className="flex flex-col gap-2 max-w-xs">
              <h2 className="text-xl font-bold text-white leading-130">
                {t("card_phone_required_title") ||
                  "Avval telefon raqamingizni kiriting"}
              </h2>
              <p className="text-sm text-white/70 leading-160">
                {t("card_phone_required_description") ||
                  "Karta qo'shish uchun avval profilingizga telefon raqamingizni ulashingiz kerak."}
              </p>
            </div>
          </div>
          <div className="pt-3">
            <BaseButton
              className="w-full disabled:!bg-white/08 disabled:!text-gray-1"
              variant="premium"
              text={t("add_phone_now") || "Telefon raqamini ulash"}
              suffix={<ArrowRight className="size-5" strokeWidth={2.2} />}
              onClick={sharePhone}
              isLoading={isSharingPhone}
              vibrate
              vibrationType="soft"
            />
          </div>
        </div>
      </PageWrapper>
    );
  }

  const handleRemove = (id: string) => {
    deleteCard(id, {
      onError: (err) => {
        showCustomToast(
          extractErrorMessage(err, t("card_delete_failed") || "Karta o'chirilmadi"),
          "error"
        );
      },
    });
  };

  const handleSetDefault = (id: string) => {
    setDefault(id, {
      onError: (err) => {
        showCustomToast(
          extractErrorMessage(err, t("set_default_failed") || "Default belgilab bo'lmadi"),
          "error"
        );
      },
    });
  };

  return (
    <PageWrapper backPath="/profile">
      <div className="container pt-safe-top pb-safe-bottom fixed inset-0 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 py-3">
          <h1 className="text-lg font-semibold text-white leading-130">
            {t("my_cards") || "Mening kartalarim"}
          </h1>
          {hasCards && (
            <motion.div layout transition={{ duration: 0.2, ease: "easeOut" }}>
              <VibratingButton
                onClick={() => setDeleteMode((v) => !v)}
                className={`h-9 rounded-full flex-center shrink-0 transition-colors duration-200 overflow-hidden ${
                  deleteMode
                    ? "text-blue px-3"
                    : "w-9 bg-white/[0.06] text-gray-1"
                }`}
              >
                <AnimatePresence mode="wait" initial={false}>
                  {deleteMode ? (
                    <motion.span
                      key="done"
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.85 }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                      className="text-sm font-semibold whitespace-nowrap"
                    >
                      {t("done")}
                    </motion.span>
                  ) : (
                    <motion.span
                      key="trash"
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.85 }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                      className="flex-center"
                    >
                      <Trash2 className="size-5" />
                    </motion.span>
                  )}
                </AnimatePresence>
              </VibratingButton>
            </motion.div>
          )}
        </div>

        {/* Cards list / empty / loading */}
        <div className="flex-1 overflow-y-auto pt-2 flex flex-col">
          {isLoading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl bg-gray-4 border border-white/08 p-3 flex items-center gap-3"
                >
                  <div className="size-11 rounded-lg skeleton bg-white/08" />
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="h-3 w-32 rounded-full skeleton" />
                    <div className="h-3 w-20 rounded-full skeleton" />
                  </div>
                </div>
              ))}
            </div>
          ) : hasCards ? (
            <div className="flex flex-col gap-2">
              <AnimatePresence initial={false}>
                {cards.map((card) => (
                  <motion.div
                    key={card.id}
                    layout
                    exit={{
                      opacity: 0,
                      height: 0,
                      marginTop: 0,
                      scale: 0.95,
                    }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    style={{ overflow: "hidden" }}
                  >
                    <BankCardItem
                      card={card}
                      onRemove={handleRemove}
                      onSetDefault={
                        !deleteMode && !card.is_default
                          ? handleSetDefault
                          : undefined
                      }
                      showRemove={deleteMode}
                      disabled={mutating}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center">
              <div className="size-16 rounded-full bg-orange-100/15 flex-center mb-4">
                <IconCreditCard className="size-8 text-orange-100" />
              </div>
              <h2 className="text-base font-semibold text-white mb-1">
                {t("no_cards_title") || "Karta yo'q"}
              </h2>
              <p className="text-xs text-gray-1 leading-150 max-w-[260px]">
                {t("no_cards_desc") ||
                  "P2P o'tkazmalardan foydalanish uchun bank kartangizni qo'shing"}
              </p>
            </div>
          )}
        </div>

        {/* Bottom: add button */}
        <div className="flex flex-col gap-3 pt-3">
          <BaseButton
            className="w-full"
            variant="premium"
            text={t("add_card") || "Karta qo'shish"}
            prefix={<Plus className="size-5" />}
            onClick={() => navigate("/profile/cards/add")}
            vibrate
            vibrationType="soft"
          />
        </div>
      </div>
    </PageWrapper>
  );
}
